import { MODALITY_LABEL } from "@src/constants/filter-constants";
import type { ProviderSlot } from "@src/interfaces/provider.interface";
import { dayjs } from "@uhg-abyss/web/tools/dayjs";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Button } from "@uhg-abyss/web/ui/Button";
import { IconSymbol } from "@uhg-abyss/web/ui/IconSymbol";
import { LoadingSpinner } from "@uhg-abyss/web/ui/LoadingSpinner";
import { Text } from "@uhg-abyss/web/ui/Text";
import { useMemo } from "react";

export interface AvailabilitySelection {
  providerId: string;
  isoDate: string;
  slot: ProviderSlot;
  modalityLabel: string;
}

interface ProviderAvailabilityPanelProps {
  isCompact?: boolean;
  isMobile?: boolean;
  providerId: string;
  providerName: string;
  visibleIsoDates: string[];
  availableDateSet: Set<string>;
  slotsByIsoDate: Record<string, ProviderSlot[]>;
  expandedDateSet: Set<string>;
  isLoadingDates: boolean;
  isLoadingSlots: boolean;
  errorMessage?: string;
  selectedSlotKey: string | null;
  isSubmittingSelection: boolean;
  isSchedulingBlocked: boolean;
  showFooter?: boolean;
  canGoPreviousDates: boolean;
  canGoNextDates: boolean;
  onToggleDateExpanded: (providerId: string, isoDate: string) => void;
  onSelectSlot: (selection: AvailabilitySelection) => void;
  onScheduleSelectedTime: (providerId: string) => void;
  onPreviousDates: (providerId: string) => void;
  onNextDates: (providerId: string) => void;
}

interface DayRow {
  isoDate: string;
  hasAvailabilityOnDate: boolean;
  hasLoadedSlots: boolean;
  groupedSlots: Array<{
    representative: ProviderSlot;
    modalities: string[];
  }>;
  displayedSlots: Array<{
    representative: ProviderSlot;
    modalities: string[];
  }>;
  isExpanded: boolean;
}

const PanelRoot = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "15px",
  width: "100%",
});

const DayGridWithNavigation = styled("div", {
  position: "relative",
  width: "100%",
  marginLeft: "-20px",
  variants: {
    isCompact: {
      true: {
        marginLeft: "0",
        padding: "0 32px",
      },
    },
    isMobile: {
      true: {},
    },
  },
  compoundVariants: [
    {
      isCompact: true,
      isMobile: true,
      css: {
        marginLeft: "-20px",
        width: "calc(100% + 40px)",
        padding: "0 30px",
      },
    },
  ],
});

const DayGrid = styled("div", {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "10px",
  variants: {
    isCompact: {
      true: {
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      },
    },
  },
});

const DatePagerButton = styled("button", {
  position: "absolute",
  top: "0",
  bottom: "0",
  width: "36px",
  minHeight: "228px",
  padding: "0",
  margin: "0",
  border: "none",
  backgroundColor: "transparent",
  color: "$app.color.text.primary",
  fontSize: "20px",
  fontWeight: 700,
  lineHeight: 1,
  borderRadius: "6px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  zIndex: 1,
  variants: {
    direction: {
      left: {
        left: "-36px",
      },
      right: {
        right: "-36px",
      },
    },
    isCompact: {
      true: {},
    },
  },
  compoundVariants: [
    {
      direction: "left",
      isCompact: true,
      css: { left: "0" },
    },
    {
      direction: "right",
      isCompact: true,
      css: { right: "0" },
    },
  ],
  "&:disabled": {
    opacity: 0.35,
    cursor: "not-allowed",
  },
});

const PagerIcon = styled("span", {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "30px",
  height: "30px",
  color: "currentColor",
});

const DayColumn = styled("div", {
  height: "228px",
  border: "1px solid $app.color.border.muted",
  borderRadius: "6px",
  padding: "10px",
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  overflow: "hidden",
});

const DayColumnLoading = styled("div", {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  flex: 1,
});

const LoadingSlotsStack = styled("div", {
  position: "relative",
  width: "100%",
});

const LoadingSlotSkeletonList = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  width: "100%",
  paddingTop: "2px",
});

const LoadingSlotSkeleton = styled("div", {
  height: "32px",
  width: "100%",
  borderRadius: "999px",
  backgroundColor: "transparent",
  border: "1px solid $app.color.border.muted",
  opacity: 1,
});

const LoadingSpinnerOverlay = styled("div", {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  pointerEvents: "none",
});

const SlotButton = styled(Button, {
  width: "100%",
  whiteSpace: "nowrap",
  variants: {
    isMobile: {
      true: {
        paddingLeft: "6px !important",
        paddingRight: "3px !important",
      },
    },
  },
});

const SlotContent = styled("span", {
  display: "grid",
  gridTemplateColumns: "104px 38px",
  alignItems: "center",
  columnGap: "8px",
  width: "150px",
  variants: {
    isMobile: {
      true: {
        gridTemplateColumns: "64px 26px",
        columnGap: "4px",
        width: "94px",
        fontSize: "12px !important",
        lineHeight: "16px !important",
      },
    },
  },
});

const SlotTime = styled("span", {
  width: "104px",
  textAlign: "center",
  variants: {
    isMobile: {
      true: {
        width: "64px",
        fontSize: "12px !important",
        lineHeight: "16px",
      },
    },
  },
});

const SlotList = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
});

const ModalityIconGroup = styled("span", {
  position: "relative",
  left: "-22px",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  width: "38px",
  gap: "0.5px",
  color: "$app.color.text.secondary",
  opacity: 0.75,
  variants: {
    isMobile: {
      true: {
        left: "-5px",
        width: "26px",
        flexBasis: "26px",
      },
    },
  },
});

const ModalityIcon = styled("span", {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "17.5px",
  height: "17.5px",
  variants: {
    isMobile: {
      true: {
        width: "12px",
        height: "12px",
      },
    },
  },
});

const Footer = styled("div", {
  display: "flex",
  justifyContent: "flex-end",
});

const AvailabilityPanelLoader = styled("div", {
  width: "100%",
  height: "100%",
  minHeight: "228px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
});

const formatTime = (time24h: string): string => {
  const [hoursRaw, minutesRaw] = time24h.split(":");
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);

  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return time24h;
  }

  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 === 0 ? 12 : hours % 12;
  return `${displayHours}:${String(minutes).padStart(2, "0")} ${period}`;
};

const groupSlotsByTime = (
  slots: ProviderSlot[],
): Array<{ representative: ProviderSlot; modalities: string[] }> => {
  const grouped = new Map<string, { representative: ProviderSlot; modalities: Set<string> }>();

  for (const slot of slots) {
    const key = [
      slot.start_time,
      slot.department_id,
      slot.appointment_type_id,
      slot.appointment_ids.join("-"),
    ].join(":");
    const existing = grouped.get(key);

    if (!existing) {
      grouped.set(key, {
        representative: slot,
        modalities: new Set(slot.careFormat ? [slot.careFormat] : []),
      });
      continue;
    }

    if (slot.careFormat) {
      existing.modalities.add(slot.careFormat);
    }
  }

  return Array.from(grouped.values())
    .sort((a, b) => {
      return a.representative.start_time.localeCompare(b.representative.start_time);
    })
    .map((entry) => {
      return {
        representative: entry.representative,
        modalities: Array.from(entry.modalities),
      };
    });
};

const buildSlotKey = (providerId: string, isoDate: string, slot: ProviderSlot): string => {
  return `${providerId}:${isoDate}:${slot.start_time}:${slot.department_id}:${slot.appointment_type_id ?? ""}:${slot.appointment_ids.join("-")}`;
};

const buildModalityLabel = (hasVirtual: boolean, hasInPerson: boolean): string => {
  if (hasVirtual && hasInPerson) {
    return MODALITY_LABEL.VIRTUAL_AND_IN_PERSON;
  }

  if (hasVirtual) {
    return MODALITY_LABEL.VIRTUAL;
  }

  return MODALITY_LABEL.IN_PERSON;
};

export const ProviderAvailabilityPanel = ({
  isCompact = false,
  isMobile = false,
  providerId,
  providerName,
  visibleIsoDates,
  availableDateSet,
  slotsByIsoDate,
  expandedDateSet,
  isLoadingDates,
  isLoadingSlots,
  errorMessage,
  selectedSlotKey,
  isSubmittingSelection,
  isSchedulingBlocked,
  showFooter = true,
  canGoPreviousDates,
  canGoNextDates,
  onToggleDateExpanded,
  onSelectSlot,
  onScheduleSelectedTime,
  onPreviousDates,
  onNextDates,
}: ProviderAvailabilityPanelProps) => {
  const shouldShowDayColumnLoader = useMemo(() => {
    return (row: {
      hasAvailabilityOnDate: boolean;
      hasLoadedSlots: boolean;
      groupedSlots: Array<{ representative: ProviderSlot; modalities: string[] }>;
    }) => {
      return (
        (row.hasAvailabilityOnDate && !row.hasLoadedSlots) ||
        (isLoadingSlots && row.hasAvailabilityOnDate && row.groupedSlots.length === 0)
      );
    };
  }, [isLoadingSlots]);

  const dayRows = useMemo(() => {
    return visibleIsoDates.map((isoDate) => {
      const hasAvailabilityOnDate = availableDateSet.has(isoDate);
      const hasLoadedSlots = Boolean(slotsByIsoDate[isoDate]);
      const rawSlots = slotsByIsoDate[isoDate] ?? [];
      const groupedSlots = groupSlotsByTime(rawSlots);
      const isExpanded = expandedDateSet.has(isoDate);
      const displayedSlots = isExpanded ? groupedSlots : groupedSlots.slice(0, 3);

      return {
        isoDate,
        hasAvailabilityOnDate,
        hasLoadedSlots,
        groupedSlots,
        displayedSlots,
        isExpanded,
      };
    });
  }, [availableDateSet, expandedDateSet, slotsByIsoDate, visibleIsoDates]);

  const hasAnyGroupedSlots = dayRows.some((row) => {
    return row.hasAvailabilityOnDate && row.groupedSlots.length > 0;
  });

  const hasAnyLoadingDayColumn = dayRows.some((row) => {
    return shouldShowDayColumnLoader(row);
  });

  const renderSlot = (
    row: DayRow,
    entry: {
      representative: ProviderSlot;
      modalities: string[];
    },
  ): React.ReactNode => {
    const slotKey = buildSlotKey(providerId, row.isoDate, entry.representative);

    const slotIdentifier = slotKey.replace(/[^a-zA-Z0-9_-]/g, "-");
    const isSelected = selectedSlotKey === slotKey;

    const hasVirtual = entry.modalities.includes("telehealth");
    const hasInPerson = entry.modalities.includes("in-person");

    const showVirtualIcon = hasVirtual || (!hasVirtual && !hasInPerson);

    const showInPersonIcon = hasInPerson || (!hasVirtual && !hasInPerson);
    const modalityLabel = buildModalityLabel(showVirtualIcon, showInPersonIcon);

    return (
      <SlotButton
        key={slotKey}
        id={`obc-provider-availability-slot-${slotIdentifier}`}
        data-obc-label={`Provider Availability Slot for provider ${providerName} on ${row.isoDate} at ${formatTime(entry.representative.start_time)}`}
        data-testid={`test-id-provider-availability-slot-${slotIdentifier}`}
        variant={isSelected ? "filled" : "outline"}
        size="sm"
        isMobile={isMobile}
        isDisabled={isSchedulingBlocked}
        onClick={() => {
          onSelectSlot({
            providerId,
            isoDate: row.isoDate,
            slot: entry.representative,
            modalityLabel,
          });
        }}
      >
        <SlotContent
          isMobile={isMobile}
          data-testid={`test-id-provider-availability-slot-content-${slotIdentifier}`}
        >
          <SlotTime isMobile={isMobile}>{formatTime(entry.representative.start_time)}</SlotTime>

          <ModalityIconGroup isMobile={isMobile} aria-label={modalityLabel}>
            {showVirtualIcon ? (
              <ModalityIcon
                isMobile={isMobile}
                aria-hidden="true"
                data-testid="test-id-provider-availability-modality-icon-virtual"
              >
                <IconSymbol
                  icon="computer"
                  variant="outlined"
                  renderMode="svg"
                  size={isMobile ? "13" : undefined}
                />
              </ModalityIcon>
            ) : null}

            {showInPersonIcon ? (
              <ModalityIcon
                isMobile={isMobile}
                aria-hidden="true"
                data-testid="test-id-provider-availability-modality-icon-in-person"
              >
                <IconSymbol
                  icon="person"
                  variant="outlined"
                  renderMode="svg"
                  size={isMobile ? "13" : undefined}
                />
              </ModalityIcon>
            ) : null}
          </ModalityIconGroup>
        </SlotContent>
      </SlotButton>
    );
  };

  const renderDayColumn = (row: DayRow): React.ReactNode => {
    const showColumnLoader = shouldShowDayColumnLoader(row);

    return (
      <DayColumn key={row.isoDate} data-testid={`test-id-day-column-${providerId}-${row.isoDate}`}>
        <Text css={{ fontWeight: "700" }}>
          <span data-testid={`test-id-day-column-label-${providerId}-${row.isoDate}`}>
            {dayjs(row.isoDate).format("ddd")}
            <span
              style={{
                marginLeft: "4px",
                fontWeight: 500,
              }}
            >
              {dayjs(row.isoDate).format("MMM D")}
            </span>
          </span>
        </Text>

        {showColumnLoader ? (
          <DayColumnLoading
            data-testid={`test-id-provider-availability-day-loader-${providerId}-${row.isoDate}`}
          >
            <LoadingSlotsStack
              data-testid={`test-id-provider-availability-day-loader-stack-${providerId}-${row.isoDate}`}
            >
              <LoadingSlotSkeletonList aria-hidden="true">
                <LoadingSlotSkeleton />
                <LoadingSlotSkeleton />
                <LoadingSlotSkeleton />
              </LoadingSlotSkeletonList>

              <LoadingSpinnerOverlay>
                <LoadingSpinner
                  size="xs"
                  ariaLoadingLabel={`Loading availability for ${providerName} on ${dayjs(row.isoDate).format("MMM D")}`}
                  isLoading={true}
                />
              </LoadingSpinnerOverlay>
            </LoadingSlotsStack>
          </DayColumnLoading>
        ) : (
          row.hasAvailabilityOnDate &&
          row.groupedSlots.length > 0 && (
            <SlotList>
              {row.displayedSlots.map((entry) => renderSlot(row, entry))}

              {row.groupedSlots.length > 3 && (
                <Button
                  variant="text"
                  size="sm"
                  id={`obc-provider-availability-toggle-${providerId}-${row.isoDate}`}
                  data-testid={`test-id-provider-availability-toggle-${providerId}-${row.isoDate}`}
                  data-obc-label={`${row.isExpanded ? "Show fewer" : "Show more"} availability slots for provider ${providerName} on ${row.isoDate}`}
                  onClick={() => {
                    onToggleDateExpanded(providerId, row.isoDate);
                  }}
                >
                  {row.isExpanded ? "Less" : "More"}
                </Button>
              )}
            </SlotList>
          )
        )}
      </DayColumn>
    );
  };

  const renderAvailabilityBody = () => {
    if (isLoadingDates) {
      return (
        <AvailabilityPanelLoader
          data-testid={`test-id-provider-availability-loading-${providerId}`}
        >
          <LoadingSpinner
            size="lg"
            ariaLoadingLabel={`Loading availability for ${providerName}`}
            isLoading={true}
          />
          <Text>Loading provider availability</Text>
        </AvailabilityPanelLoader>
      );
    }

    if (errorMessage) {
      return (
        <AvailabilityPanelLoader data-testid={`test-id-provider-availability-error-${providerId}`}>
          <Text>{errorMessage}</Text>
        </AvailabilityPanelLoader>
      );
    }

    if (!hasAnyGroupedSlots && !hasAnyLoadingDayColumn) {
      return (
        <AvailabilityPanelLoader
          data-testid={`test-id-provider-availability-no-slots-${providerId}`}
        >
          <Text>There are no slots available for this provider</Text>
        </AvailabilityPanelLoader>
      );
    }

    return (
      <DayGridWithNavigation
        isCompact={isCompact}
        isMobile={isMobile}
        data-testid={`test-id-provider-availability-date-grid-${providerId}`}
      >
        <DatePagerButton
          type="button"
          direction="left"
          isCompact={isCompact}
          disabled={!canGoPreviousDates}
          id={`obc-provider-availability-prev-dates-${providerId}`}
          data-testid={`test-id-provider-availability-prev-dates-${providerId}`}
          aria-label={`Previous availability dates for ${providerName}`}
          data-obc-label={`Provider Availability Previous Dates for provider ${providerName}`}
          onClick={() => {
            onPreviousDates(providerId);
          }}
        >
          <PagerIcon aria-hidden="true">
            <IconSymbol icon="chevron_left" size="36" renderMode="svg" />
          </PagerIcon>
        </DatePagerButton>
        <DayGrid
          isCompact={isCompact}
          data-testid={`test-id-provider-availability-date-columns-${providerId}`}
        >
          {dayRows.map(renderDayColumn)}
        </DayGrid>
        <DatePagerButton
          type="button"
          direction="right"
          isCompact={isCompact}
          disabled={!canGoNextDates}
          id={`obc-provider-availability-next-dates-${providerId}`}
          data-testid={`test-id-provider-availability-next-dates-${providerId}`}
          aria-label={`Next availability dates for ${providerName}`}
          data-obc-label={`Provider Availability Next Dates for provider ${providerName}`}
          onClick={() => {
            onNextDates(providerId);
          }}
        >
          <PagerIcon aria-hidden="true">
            <IconSymbol icon="chevron_right" size="36" renderMode="svg" />
          </PagerIcon>
        </DatePagerButton>
      </DayGridWithNavigation>
    );
  };

  return (
    <PanelRoot data-testid={`test-id-provider-availability-${providerId}`}>
      {renderAvailabilityBody()}
      {showFooter && (
        <Footer>
          <Button
            variant="filled"
            isDisabled={
              !selectedSlotKey || isSubmittingSelection || isLoadingDates || isSchedulingBlocked
            }
            id={`obc-provider-availability-schedule-selected-${providerId}`}
            data-testid={`test-id-provider-availability-${providerId}-schedule-selected-time-button`}
            data-obc-label={`Schedule Selected Time for provider ${providerName}`}
            onClick={() => {
              onScheduleSelectedTime(providerId);
            }}
          >
            Schedule selected time
          </Button>
        </Footer>
      )}
    </PanelRoot>
  );
};
